import React, { useState } from 'react'
import { PressEvent } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { Button } from '../Button/Button'
import { Flex } from '../Flex'

export type PaginationConfig = {
  page: number
  totalCount: number
  pageSize?: number
  setPage: (p: number) => void
  /** Callback when page changes */
  onPageChange?: (page: number) => void
}

type TablePaginationProps = {
  pagination: PaginationConfig
}

export const defaultOptions = {
  pageSize: 20,
}

export function TablePagination({ pagination }: TablePaginationProps) {
  const { t } = useTranslation()
  const { totalCount, page = 0, setPage, onPageChange } = pagination
  const paginationMax = 3
  const options = {
    ...defaultOptions,
    ...pagination,
  }

  const lastPageIndex = Math.ceil(totalCount / options.pageSize) - 1

  const createPointer = (index: number, isCollapsed?: boolean) => {
    return (
      <Pointer
        key={index}
        label={isCollapsed ? '...' : index + 1}
        isActive={page === index}
        onPress={() => {
          if (onPageChange) {
            onPageChange(index)
          }

          setPage(index)
        }}
      />
    )
  }

  const pointers = [createPointer(0)]
  if (page - paginationMax > 1) {
    pointers.push(createPointer(1, true))
  }

  for (let i = page - paginationMax; i <= page + paginationMax; i++) {
    if (i > 0 && i < lastPageIndex) {
      pointers.push(createPointer(i))
    }
  }
  if (page + paginationMax < lastPageIndex - 1) {
    pointers.push(createPointer(page + paginationMax + 1, true))
  }

  if (lastPageIndex > 0) {
    pointers.push(createPointer(lastPageIndex))
  }

  return (
    <Flex align="center" justify="space-between">
      {totalCount > options.pageSize && <ul className="react-aria-Pagination">{pointers}</ul>}
      <span>{`${t('rowCount', { count: totalCount })}`}</span>
    </Flex>
  )
}

type PointerProps = {
  label: string | number
  onPress(event: PressEvent): void
  isActive: boolean
}

const Pointer: React.FC<PointerProps> = ({ label, onPress, isActive }) => (
  <li className={isActive ? 'react-aria-Pagination-active' : ''}>
    <Button onPress={onPress}>{label}</Button>
  </li>
)

export function usePagination(
  rows: object[],
  userConfig?: PaginationConfig,
): [PaginationConfig, () => object[]] {
  const [page, setPage] = useState(0)
  const props = {
    ...defaultOptions,
    ...userConfig,
    page,
    totalCount: rows.length,
    setPage,
  }

  const getPageRows = () => {
    return rows.slice(page * props.pageSize, (page + 1) * props.pageSize)
  }

  return [props, getPageRows]
}
